import React, { useState, useEffect, useRef, useMemo, useDeferredValue, useCallback, memo } from 'react';
import styles from '../../asset/styles/hotels/hotels.module.scss';
import IconsColor from '../../util/iconsColor';
import Popup from '../../component/popup/popup';
import { agent } from 'apiClient/api';
import { useSnackbar } from 'context/snackbarContext';
import { useLoader } from 'context/loaderContext';
import Loader from 'component/loader/loaderComponent';
import { HotelsPopUp } from './HotelsPopUp';
import { useHotelGeneric } from 'context/HotelGenericContext';
import { HotelListModel } from '../../models';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import CustomizedMenu from '../../component/muiMenu/muiMenu';
import MainTabsTableHeader, { FilterGroup, SortOption } from '../../component/mainTabsTableHeader/mainTabsTableHeader';
import SortIndicator, { sortIndicatorClasses } from '../../component/sortIndicator/SortIndicator';
import {
  applyFilters,
  applySearch,
  applySort,
  cycleSort,
  sortByNumberAsc,
  sortByNumberDesc,
  sortRowsBy,
  SortState,
  uniqueOptionsFromRows,
} from '../../util/listFilters';
import { useListView, LIST_VIEW_KEYS } from '../../context/listFilterContext';
import { toTitleCase } from 'util/textcase';
import { TablePaginationBar, useClientListPagination } from '../../component/tablePagination';
import utilStyles from '../../asset/styles/util/util.module.scss';
import {
  DUPLICATE_HOTEL_NAME_MESSAGE,
  hotelNameExistsInCity,
} from './hotelNameExistsInCity';

// ----- Module-level pure helpers (used by memoized row; safe to leave outside component) -----
const CONTRACT_STATUS_NO_RATES_ID = 4
const CONTRACT_STATUS_NO_RATES_NAME = 'No Rates'

/** Hardcoded backup when generic-data `contract_status` is missing/empty. */
const CONTRACT_STATUS_FILTER_FALLBACK: { label: string; value: string }[] = [
  { label: 'Active', value: '1' },
  { label: 'Expired', value: '3' },
  { label: 'Expiring Soon', value: '2' },
  { label: 'No Rates', value: '4' },
  { label: 'Upcoming', value: '5' },
]

const getEffectiveContractStatus = (hotel: HotelListModel): { id: number; name: string } => {
  if (hotel.room_price == null) {
    return { id: CONTRACT_STATUS_NO_RATES_ID, name: CONTRACT_STATUS_NO_RATES_NAME }
  }
  return {
    id: hotel.contract_status_id,
    name: hotel.contract_status_name || 'Unknown Status',
  }
}

const getStatusClass = (statusId: number): string => {
  switch (statusId) {
    case 1: return styles.statusActive;
    case 2: return styles.statusExpiringSoon;
    case 3: return styles.statusExpired;
    case 4: return styles.statusNoRates;
    case 5: return styles.statusUpcoming;
    default: return '';
  }
};

// ----- Memoized row -----
// Each row is its own React.memo component so typing in the search bar (which only flips
// page-level state) does NOT cause 179 rows × all their MUI menus + SVG icons to re-render.
// Hover state lives locally per row so hovering one row doesn't ripple to the other 178.
interface HotelRowProps {
  hotel: HotelListModel;
  contractStatusName: string;
  onNavigate: (hotelId: string) => void;
  onSetStatus: (hotel: HotelListModel) => void;
  onDelete: (hotel: HotelListModel) => void;
}

const HotelRow = memo<HotelRowProps>(({ hotel, contractStatusName, onNavigate, onSetStatus, onDelete }) => {
  const [hovered, setHovered] = useState(false);
  const effectiveContractStatus = useMemo(() => getEffectiveContractStatus(hotel), [hotel]);
  const lastUpdated = useMemo(
    () => moment(hotel.last_updated_at_timestamp).format('DD-MMM-YYYY hh:mm A'),
    [hotel.last_updated_at_timestamp],
  );
  const menuItems = useMemo(
    () => [
      {
        label: `Set Status to ${hotel.flag_active ? 'Inactive' : 'Active'}`,
        onClick: () => onSetStatus(hotel),
      },
      {
        label: 'Delete Hotel',
        onClick: () => onDelete(hotel),
      },
    ],
    [hotel, onSetStatus, onDelete],
  );
  return (
    <tr className={getStatusClass(effectiveContractStatus.id)}>
      <td className={styles.hotelNameCell}>
        <div
          className={styles.statusIndicator}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          {hovered && <span className={styles.statusTooltip}>{contractStatusName}</span>}
        </div>
        <span className={styles.hotelName} onClick={() => onNavigate(hotel.hotel_id)}>{toTitleCase(hotel.hotel_name)}</span>
      </td>
      <td>
        <span className={styles.locationCell}>
          <IconsColor iconName="location" width="14" height="14" fillColor="none" strokeColor="#7a7a7a" />
          {toTitleCase(hotel.city_name)}
        </span>
      </td>
      <td>
        {hotel.rating ? (
          <span className={styles.ratingCell}>
            {parseInt(hotel.rating?.toString() ?? '0')}
            <IconsColor iconName="star" width="16" height="16" fillColor="#FFC107" strokeColor="#FFC107" />
          </span>
        ) : (
          <span>-</span>
        )}
      </td>
      <td>{toTitleCase(hotel.hotel_type_name)}</td>
      <td>
        <span className={hotel.flag_active ? styles.statusBadgeActive : styles.statusBadgeInactive}>
          {hotel.flag_active ? 'Active' : 'Inactive'}
        </span>
      </td>
      <td>{lastUpdated}</td>
      <td>{hotel.updated_by_user_name}</td>
      <td style={{ position: 'relative' }}>
        <CustomizedMenu
          buttonText=""
          menuItems={menuItems}
          buttonVariant="text"
          buttonStyle={{ padding: '0px', minWidth: '0px' }}
          buttonEndIcon={<IconsColor iconName="moreVert" width="20" height="20" fillColor="none" strokeColor="#7a7a7a" />}
        />
      </td>
    </tr>
  );
});
HotelRow.displayName = 'HotelRow';

// Clickable header cell for the Hotels raw <table>. Mirrors the indicator behavior used by
// the shared Table component. When `disabled` (Price dropdown active), clicks are inert and
// the hover affordance disappears so the user understands column sort is overridden.
interface SortableHotelThProps {
  label: string;
  sortKey: string;
  sortState: SortState;
  onSort: (key: string) => void;
  disabled?: boolean;
}
const SortableHotelTh: React.FC<SortableHotelThProps> = ({ label, sortKey, sortState, onSort, disabled }) => {
  const isActive = !disabled && sortState.key === sortKey && sortState.direction != null;
  const indicatorDirection: 'asc' | 'desc' | null = isActive ? sortState.direction : null;
  const thClassName = [
    sortIndicatorClasses.sortable,
    isActive ? sortIndicatorClasses.active : '',
    disabled ? sortIndicatorClasses.disabled : '',
  ].filter(Boolean).join(' ');
  return (
    <th
      className={thClassName}
      onClick={disabled ? undefined : () => onSort(sortKey)}
      style={{ opacity: disabled ? 0.6 : 1 }}
      aria-sort={isActive ? (sortState.direction === 'asc' ? 'ascending' : 'descending') : (disabled ? undefined : 'none')}
    >
      {label}
      <SortIndicator direction={indicatorDirection} />
    </th>
  );
};

const Hotels: React.FC = () => {

  const [isHotelPopupOpen, setIsHotelPopupOpen] = useState(false);
  const [isConfirmationPopupOpen, setIsConfirmationPopupOpen] = useState(false);
  const [confirmationType, setConfirmationType] = useState<'status' | 'delete' | null>(null);
  const [selectedHotelForAction, setSelectedHotelForAction] = useState<HotelListModel | null>(null);
  const { showError, showSuccess } = useSnackbar();
  const { setIsLoading, isLoading } = useLoader();
  const { hotelGenericData, setHotelGenericData } = useHotelGeneric();
  const [hotels, setHotels] = useState<HotelListModel[]>([]);
  const [newHotelData, setNewHotelData] = useState({ hotel_name: '', city_id: 0 });
  const isDuplicateHotelName = hotelNameExistsInCity(
    hotels,
    newHotelData.hotel_name,
    newHotelData.city_id,
  );
  // ----- Header (search / sort / filters) state — persisted across navigation -----
  const {
    search: searchValue, setSearch: setSearchValue,
    sortValue: tableSortValue, setSortValue: setTableSortValue,
    sortState, setSortState,
    filters: tableSelectedFilters, setFilters: setTableSelectedFilters,
    page, setPage, pageSize, setPageSize,
  } = useListView(LIST_VIEW_KEYS.hotels);
  // Column-header sort lives alongside the Price dropdown. Price dropdown wins:
  // when tableSortValue !== 'default', column clicks are ignored and arrows hidden.
  const isPriceSortActive = tableSortValue !== 'default';
  const handleColumnSort = useCallback(
    (key: string) => {
      if (isPriceSortActive) return; // Price dropdown is the active sort; ignore column clicks.
      setSortState((prev) => cycleSort(prev, key));
    },
    [isPriceSortActive, setSortState],
  );
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const GetHotelList = async () => {
    try {
      setIsLoading(true);
      const response = await agent.HotelListAPI.get();
      if (response?.status === false) {
        showError(response?.error?.message);
        setIsLoading(false);
      } else {
        // showSuccess('Hotel list fetched successfully');
        setIsLoading(false);
        setHotels(response?.data);
      }
    } catch (error) {
      showError('Failed to fetch hotel list');
      setIsLoading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;

    const loadInitialHotelsPageData = async () => {
      setIsLoading(true);
      try {
        const [hotelListRes, genericRes] = await Promise.all([
          agent.HotelListAPI.get(),
          agent.GenericGlobalAPI.get(),
        ]);

        if (cancelled) return;

        if (hotelListRes?.status === false) {
          showError(hotelListRes?.error?.message || 'Failed to fetch hotel list');
        } else {
          setHotels(hotelListRes?.data ?? []);
        }

        if (genericRes?.status === false) {
          showError(genericRes?.error || 'Failed to fetch generic hotel details');
        } else {
          setHotelGenericData(genericRes?.data[0]);
        }
      } catch (error) {
        if (!cancelled) {
          showError('Failed to load hotels');
        }
      } finally {
        // Keep loader visible until next frame so table state is painted first.
        await new Promise<void>((resolve) => {
          requestAnimationFrame(() => resolve());
        });
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadInitialHotelsPageData();

    return () => {
      cancelled = true;
      setIsLoading(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddNewHotel = (open: boolean) => {
    setIsHotelPopupOpen(open);
    if (!open) {
      // Reset form data when closing
      setNewHotelData({ hotel_name: '', city_id: 0 });
    }
  };

  const handleFormChange = (hotelName: string, cityId: number) => {
    setNewHotelData({ hotel_name: toTitleCase(hotelName), city_id: cityId });
  };

  const handleCreateHotel = async () => {
    // Validationclose
    try {
      setIsLoading(true);
      const response = await agent.CreateHotelAPI.post({ ...newHotelData, hotel_name: newHotelData.hotel_name.trim() });

      if (response?.status === false) {
        showError(response?.error?.message || 'Failed to create hotel');
        setIsLoading(false);
      } else {
        showSuccess('Hotel created successfully');
        setIsLoading(false);
        handleAddNewHotel(false);
        navigate(`/hotels/${response?.data[0]?.hotel_id}`, { state: { tab: 'hotel-details' } })
        // Refresh the hotel list
        return;
      }
    } catch (error) {
      showError('Failed to create hotel');
      setIsLoading(false);
    }
  };

  // ----- Filter accessors -----
  const FILTER_ACCESSORS = useMemo(
    () => ({
      city: (row: HotelListModel) => row.city_name,
      // Normalize rating to integer-string so 3, 3.0, "3", "3.5", "3 Star" all match filter value "3".
      rating: (row: HotelListModel) => {
        const n = parseInt(String(row.rating ?? ''), 10)
        return Number.isNaN(n) ? '' : String(n)
      },
      type: (row: HotelListModel) => row.hotel_type_name,
      editor: (row: HotelListModel) => row.updated_by_user_name,
      contract_status: (row: HotelListModel) => String(getEffectiveContractStatus(row).id),
      status: (row: HotelListModel) => (row.flag_active ? 'Active' : 'Inactive'),
    }),
    [],
  );

  const contractStatusFilterOptions = useMemo(() => {
    const fromGeneric = hotelGenericData?.contract_status ?? []
    if (fromGeneric.length > 0) {
      return fromGeneric.map((x) => ({
        label: x.contract_status_name,
        value: String(x.contract_status_id),
      }))
    }
    return CONTRACT_STATUS_FILTER_FALLBACK
  }, [hotelGenericData?.contract_status])

  // ----- Filter groups (per-tab spec: City, Rating 3/4/5, Type, Editor, Contract Status, Status) -----
  const filterGroups: FilterGroup[] = useMemo(
    () => [
      { key: 'city', label: 'City', options: uniqueOptionsFromRows(hotels, FILTER_ACCESSORS.city) },
      {
        key: 'rating',
        label: 'Rating',
        options: [
          { label: '3 Stars', value: '3' },
          { label: '4 Stars', value: '4' },
          { label: '5 Stars', value: '5' },
        ],
      },
      { key: 'type', label: 'Type', options: uniqueOptionsFromRows(hotels, FILTER_ACCESSORS.type) },
      { key: 'editor', label: 'Editor', options: uniqueOptionsFromRows(hotels, FILTER_ACCESSORS.editor) },
      { key: 'contract_status', label: 'Contract Status', options: contractStatusFilterOptions },
      {
        key: 'status',
        label: 'Status',
        options: [
          { label: 'Active', value: 'Active' },
          { label: 'Inactive', value: 'Inactive' },
        ],
      },
    ],
    [hotels, FILTER_ACCESSORS, contractStatusFilterOptions],
  );

  // ----- Sort options (Hotels keeps Price-only dropdown per spec) -----
  const sortOptions: SortOption[] = [
    { label: 'Sort', value: 'default' },
    { label: 'Price: Low to High', value: 'price_asc' },
    { label: 'Price: High to Low', value: 'price_desc' },
  ];

  // ----- Pipeline: filter → search → sort -----
  // Deferred values keep the search/filter UI snappy when the user clears or types fast.
  // React renders the heavy table re-render at low priority while the input updates instantly.
  const deferredSearch = useDeferredValue(searchValue);
  const deferredFilters = useDeferredValue(tableSelectedFilters);
  const sortedHotels = useMemo(() => {
    let rows = applyFilters(hotels, deferredFilters, FILTER_ACCESSORS);
    rows = applySearch(rows, deferredSearch, [(h) => h.hotel_name, (h) => h.city_name]);
    if (isPriceSortActive) {
      // Price dropdown wins over any column-header click.
      rows = applySort(rows, tableSortValue, {
        price_asc: sortByNumberAsc<HotelListModel>((h) => h.room_price ?? 0),
        price_desc: sortByNumberDesc<HotelListModel>((h) => h.room_price ?? 0),
      });
    } else {
      rows = sortRowsBy(rows, sortState);
    }
    return rows;
  }, [hotels, deferredFilters, deferredSearch, tableSortValue, isPriceSortActive, sortState, FILTER_ACCESSORS]);

  const paginationQueryKey = JSON.stringify({
    search: searchValue,
    filters: tableSelectedFilters,
    sortState,
    sortValue: tableSortValue,
  });
  const {
    pagedRows: pagedHotels,
    total: pagedTotal,
    page: currentPage,
    pageSize: currentPageSize,
    handlePageChange,
    handlePageSizeChange,
  } = useClientListPagination(sortedHotels, {
    page, pageSize, setPage, setPageSize, queryKey: paginationQueryKey,
  });

  // Stable map { contract_status_id -> contract_status_name }. Built once per generic-data load
  // so memoized row components can look up their status name with no extra computation.
  const contractStatusById = useMemo(() => {
    const map = new Map<number, string>();
    ((hotelGenericData?.contract_status as { contract_status_id: number; contract_status_name: string }[] | undefined) || []).forEach((x) =>
      map.set(Number(x.contract_status_id), x.contract_status_name),
    );
    return map;
  }, [hotelGenericData]);


  // Handle click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null);
      }
    };

    if (openMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenuId]);

  // Handlers wrapped in useCallback so HotelRow's React.memo can short-circuit re-renders.
  // Without stable references, every keystroke creates new function instances → memo defeats.
  const handleSetStatusClick = useCallback((hotel: HotelListModel) => {
    setSelectedHotelForAction(hotel);
    setConfirmationType('status');
    setIsConfirmationPopupOpen(true);
    setOpenMenuId(null);
  }, []);

  const handleDeleteClick = useCallback((hotel: HotelListModel) => {
    setSelectedHotelForAction(hotel);
    setConfirmationType('delete');
    setIsConfirmationPopupOpen(true);
    setOpenMenuId(null);
  }, []);

  const handleNavigateToHotel = useCallback((hotelId: string) => {
    navigate(`/hotels/${hotelId}`);
  }, [navigate]);

  // Confirm status change
  const handleConfirmStatusChange = async () => {
    if (!selectedHotelForAction) return;

    try {
      setIsLoading(true);
      setIsConfirmationPopupOpen(false);
      const response = await agent.DeleteHotelAPI.put({
        hotel_id: selectedHotelForAction.hotel_id,
        flag_active: !selectedHotelForAction.flag_active,
        hotel_flag_deleted: false
      });
      if (response?.status === false) {
        showError(response?.error?.message || 'Failed to update hotel status');
      } else {
        showSuccess('Hotel status updated successfully');
        setSelectedHotelForAction(null);
        setConfirmationType(null);
        GetHotelList(); // Refresh the list
      }
      setIsLoading(false);
    } catch (error) {
      showError('Failed to update hotel status');
      setIsLoading(false);
    }
  };

  // Confirm delete hotel
  const handleConfirmDelete = async () => {
    if (!selectedHotelForAction) return;

    try {
      setIsLoading(true);
      setIsConfirmationPopupOpen(false);
      const response = await agent.DeleteHotelAPI.put({
        hotel_id: selectedHotelForAction.hotel_id,
        flag_active: selectedHotelForAction.flag_active,
        hotel_flag_deleted: true
      });
      if (response?.status === false) {
        showError(response?.error?.message || 'Failed to delete hotel');
      } else {
        showSuccess('Hotel deleted successfully');
        setSelectedHotelForAction(null);
        setConfirmationType(null);
        GetHotelList(); // Refresh the list
      }
      setIsLoading(false);
    } catch (error) {
      showError('Failed to delete hotel');
      setIsLoading(false);
    }
  };

  // Cancel confirmation popup
  const handleCancelConfirmation = () => {
    setIsConfirmationPopupOpen(false);
    setSelectedHotelForAction(null);
    setConfirmationType(null);
  };

  return (<>
    {isLoading && <Loader message="Loading..." overlay={true} />}

    <Popup
      isOpen={isHotelPopupOpen}
      onClose={() => handleAddNewHotel(false)}
      title="Add New Hotel"
      primaryButton={{
        label: 'Continue',
        onClick: handleCreateHotel,
        disabled: newHotelData.hotel_name === '' || newHotelData.city_id === 0 || isDuplicateHotelName,
      }}
      heightFiexd={true}
    >
      <HotelsPopUp
        onFormChange={handleFormChange}
        hotelNameError={isDuplicateHotelName ? DUPLICATE_HOTEL_NAME_MESSAGE : undefined}
      />
    </Popup>

    <Popup
      isOpen={isConfirmationPopupOpen}
      onClose={handleCancelConfirmation}
      title={confirmationType === 'status' ? 'Change Hotel Status' : 'Delete Hotel'}
      primaryButton={{
        label: 'Confirm',
        onClick: confirmationType === 'status' ? handleConfirmStatusChange : handleConfirmDelete,
      }}
      secondaryButton={{
        label: 'Cancel',
        onClick: handleCancelConfirmation,
      }}
      heightFiexd={true}
    >
      <HotelsPopUp
        confirmationType={confirmationType}
        hotel={selectedHotelForAction}
      />
    </Popup>

    <div className={`${styles.pageContainer} ${utilStyles.listPageFill}`}>
      <div className={utilStyles.listPageHeader}>
      <MainTabsTableHeader
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        searchPlaceholder="Search hotels"
        totalCount={hotels.length}
        filteredCount={sortedHotels.length}
        itemLabel="hotels"
        sortValue={tableSortValue}
        onSortChange={setTableSortValue}
        sortOptions={sortOptions}
        filterGroups={filterGroups}
        selectedFilters={tableSelectedFilters}
        onFiltersChange={setTableSelectedFilters}
        addButtonLabel="Hotel"
        onAddClick={() => handleAddNewHotel(true)}
        showResultCount={false}
      />
      </div>

      <div className={utilStyles.listPageTableBlock}>
      <div className={styles.hotelsTableContainer}>
        <table className={styles.hotelsTable}>
          <thead>
            <tr>
              <SortableHotelTh label="Hotel Name" sortKey="hotel_name" sortState={sortState} onSort={handleColumnSort} disabled={isPriceSortActive} />
              <SortableHotelTh label="City" sortKey="city_name" sortState={sortState} onSort={handleColumnSort} disabled={isPriceSortActive} />
              <SortableHotelTh label="Rating" sortKey="rating" sortState={sortState} onSort={handleColumnSort} disabled={isPriceSortActive} />
              <SortableHotelTh label="Type" sortKey="hotel_type_name" sortState={sortState} onSort={handleColumnSort} disabled={isPriceSortActive} />
              <SortableHotelTh label="Status" sortKey="flag_active" sortState={sortState} onSort={handleColumnSort} disabled={isPriceSortActive} />
              <SortableHotelTh label="Last Updated" sortKey="last_updated_at_timestamp" sortState={sortState} onSort={handleColumnSort} disabled={isPriceSortActive} />
              <SortableHotelTh label="Editor" sortKey="updated_by_user_name" sortState={sortState} onSort={handleColumnSort} disabled={isPriceSortActive} />
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sortedHotels.length === 0 ? (
              <tr>
                <td colSpan={8} className={styles.noDataMessage} style={{ textAlign: 'center' }}>
                  No hotels found
                </td>
              </tr>
            ) : (
              pagedHotels.map((hotel: HotelListModel) => {
                const effectiveContractStatus = getEffectiveContractStatus(hotel)
                return (
                <HotelRow
                  key={hotel.hotel_id}
                  hotel={hotel}
                  contractStatusName={
                    contractStatusById.get(effectiveContractStatus.id) || effectiveContractStatus.name
                  }
                  onNavigate={handleNavigateToHotel}
                  onSetStatus={handleSetStatusClick}
                  onDelete={handleDeleteClick}
                />
                )
              })
            )}
          </tbody>
        </table>
      </div>
      <TablePaginationBar
        total={pagedTotal}
        page={currentPage}
        pageSize={currentPageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
      </div>
    </div>
  </>
  );
};

export default Hotels;